import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getFinancialStats,
  getExpandedFinancialStats,
  getIRReport,
  getInvoices,
  getConsultationFinancial,
  updateConsultationFinancial,
  FinancialStats,
  ReceitaIR,
  NotaFiscal,
  createTransaction,
  getTransaction,
  getTransactions,
  updateTransaction,
  deleteTransaction,
  createCategory,
  getCategories,
  updateCategory,
  deleteCategory,
  initializeDefaultCategories,
  createAccount,
  getAccounts,
  updateAccount,
  deleteAccount,
  createBudget,
  getBudgets,
  updateBudget,
  deleteBudget,
  getCashFlowData,
  getCategorySummary,
  getMonthlyReport,
  syncConsultationsAsTransactions,
} from '../services/financialService';
import {
  Transaction,
  TransactionCategory,
  FinancialAccount,
  Budget,
  CashFlowData,
  CategorySummary,
  MonthlyReport,
} from '../types/financial';

export function useFinancial() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<FinancialStats | null>(null);
  const [irReport, setIrReport] = useState<ReceitaIR[]>([]);
  const [invoices, setInvoices] = useState<NotaFiscal[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categories, setCategories] = useState<TransactionCategory[]>([]);
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);

  const loadStats = useCallback(
    async (month?: number, year?: number, expanded = true) => {
      if (!user?.uid) return;

      setLoading(true);
      setError(null);
      try {
        const financialStats = expanded
          ? await getExpandedFinancialStats(user.uid, month, year)
          : await getFinancialStats(user.uid, month, year);
        setStats(financialStats);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar estatísticas financeiras');
        console.error('Erro ao carregar estatísticas:', err);
      } finally {
        setLoading(false);
      }
    },
    [user?.uid]
  );

  const loadIRReport = useCallback(
    async (year: number) => {
      if (!user?.uid) return;

      setLoading(true);
      setError(null);
      try {
        const report = await getIRReport(user.uid, year);
        setIrReport(report);
      } catch (err: any) {
        setError(err.message || 'Erro ao gerar relatório IR');
        console.error('Erro ao gerar relatório IR:', err);
      } finally {
        setLoading(false);
      }
    },
    [user?.uid]
  );

  const loadInvoices = useCallback(async () => {
    if (!user?.uid) return;

    setLoading(true);
    setError(null);
    try {
      const invoiceList = await getInvoices(user.uid);
      setInvoices(invoiceList);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar notas fiscais');
      console.error('Erro ao carregar notas fiscais:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  const updateConsultationValue = useCallback(
    async (
      patientId: string,
      consultationId: string,
      data: {
        valor?: number;
        pago?: boolean;
        dataPagamento?: string;
      }
    ) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await updateConsultationFinancial(user.uid, patientId, consultationId, data);
        // Recarregar estatísticas após atualização
        await loadStats();
      } catch (err: any) {
        setError(err.message || 'Erro ao atualizar valor da consulta');
        console.error('Erro ao atualizar valor:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadStats]
  );

  const getConsultationValue = useCallback(
    async (patientId: string, consultationId: string) => {
      if (!user?.uid) return null;

      try {
        return await getConsultationFinancial(user.uid, patientId, consultationId);
      } catch (err: any) {
        console.error('Erro ao buscar valor da consulta:', err);
        return null;
      }
    },
    [user?.uid]
  );

  // ===== Transações =====
  const loadTransactions = useCallback(
    async (filters?: {
      type?: 'receita' | 'despesa';
      categoryId?: string;
      accountId?: string;
      startDate?: string;
      endDate?: string;
      status?: 'pendente' | 'pago' | 'cancelado';
      limit?: number;
    }) => {
      if (!user?.uid) return;

      setLoading(true);
      setError(null);
      try {
        const transactionsData = await getTransactions(user.uid, filters);
        setTransactions(transactionsData);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar transações');
        console.error('Erro ao carregar transações:', err);
      } finally {
        setLoading(false);
      }
    },
    [user?.uid]
  );

  const createNewTransaction = useCallback(
    async (transaction: Omit<Transaction, 'id' | 'createdAt' | 'updatedAt'>) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await createTransaction(user.uid, transaction);
        await loadTransactions();
        await loadStats();
      } catch (err: any) {
        setError(err.message || 'Erro ao criar transação');
        console.error('Erro ao criar transação:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadTransactions, loadStats]
  );

  const updateTransactionData = useCallback(
    async (transactionId: string, updates: Partial<Transaction>) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await updateTransaction(user.uid, transactionId, updates);
        await loadTransactions();
        await loadStats();
      } catch (err: any) {
        setError(err.message || 'Erro ao atualizar transação');
        console.error('Erro ao atualizar transação:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadTransactions, loadStats]
  );

  const deleteTransactionData = useCallback(
    async (transactionId: string) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await deleteTransaction(user.uid, transactionId);
        await loadTransactions();
        await loadStats();
      } catch (err: any) {
        setError(err.message || 'Erro ao excluir transação');
        console.error('Erro ao excluir transação:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadTransactions, loadStats]
  );

  // ===== Categorias =====
  const loadCategories = useCallback(
    async (type?: 'receita' | 'despesa') => {
      if (!user?.uid) return;

      setLoading(true);
      setError(null);
      try {
        await initializeDefaultCategories(user.uid);
        const categoriesData = await getCategories(user.uid, type);
        setCategories(categoriesData);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar categorias');
        console.error('Erro ao carregar categorias:', err);
      } finally {
        setLoading(false);
      }
    },
    [user?.uid]
  );

  const createNewCategory = useCallback(
    async (category: Omit<TransactionCategory, 'id' | 'createdAt' | 'updatedAt'>) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await createCategory(user.uid, category);
        await loadCategories();
      } catch (err: any) {
        setError(err.message || 'Erro ao criar categoria');
        console.error('Erro ao criar categoria:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadCategories]
  );

  const updateCategoryData = useCallback(
    async (categoryId: string, updates: Partial<TransactionCategory>) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await updateCategory(user.uid, categoryId, updates);
        await loadCategories();
      } catch (err: any) {
        setError(err.message || 'Erro ao atualizar categoria');
        console.error('Erro ao atualizar categoria:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadCategories]
  );

  const deleteCategoryData = useCallback(
    async (categoryId: string) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await deleteCategory(user.uid, categoryId);
        await loadCategories();
      } catch (err: any) {
        setError(err.message || 'Erro ao excluir categoria');
        console.error('Erro ao excluir categoria:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadCategories]
  );

  // ===== Contas =====
  const loadAccounts = useCallback(async () => {
    if (!user?.uid) return;

    setLoading(true);
    setError(null);
    try {
      const accountsData = await getAccounts(user.uid);
      setAccounts(accountsData);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar contas');
      console.error('Erro ao carregar contas:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  const createNewAccount = useCallback(
    async (account: Omit<FinancialAccount, 'id' | 'currentBalance' | 'createdAt' | 'updatedAt'>) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await createAccount(user.uid, account);
        await loadAccounts();
      } catch (err: any) {
        setError(err.message || 'Erro ao criar conta');
        console.error('Erro ao criar conta:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadAccounts]
  );

  const updateAccountData = useCallback(
    async (accountId: string, updates: Partial<FinancialAccount>) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await updateAccount(user.uid, accountId, updates);
        await loadAccounts();
      } catch (err: any) {
        setError(err.message || 'Erro ao atualizar conta');
        console.error('Erro ao atualizar conta:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadAccounts]
  );

  const deleteAccountData = useCallback(
    async (accountId: string) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await deleteAccount(user.uid, accountId);
        await loadAccounts();
      } catch (err: any) {
        setError(err.message || 'Erro ao excluir conta');
        console.error('Erro ao excluir conta:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadAccounts]
  );

  // ===== Metas =====
  const loadBudgets = useCallback(
    async (year?: number, month?: number) => {
      if (!user?.uid) return;

      setLoading(true);
      setError(null);
      try {
        const budgetsData = await getBudgets(user.uid, year, month);
        setBudgets(budgetsData);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar metas');
        console.error('Erro ao carregar metas:', err);
      } finally {
        setLoading(false);
      }
    },
    [user?.uid]
  );

  const createNewBudget = useCallback(
    async (budget: Omit<Budget, 'id' | 'createdAt' | 'updatedAt'>) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await createBudget(user.uid, budget);
        await loadBudgets();
      } catch (err: any) {
        setError(err.message || 'Erro ao criar meta');
        console.error('Erro ao criar meta:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadBudgets]
  );

  const updateBudgetData = useCallback(
    async (budgetId: string, updates: Partial<Budget>) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await updateBudget(user.uid, budgetId, updates);
        await loadBudgets();
      } catch (err: any) {
        setError(err.message || 'Erro ao atualizar meta');
        console.error('Erro ao atualizar meta:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadBudgets]
  );

  const deleteBudgetData = useCallback(
    async (budgetId: string) => {
      if (!user?.uid) throw new Error('Usuário não autenticado');

      setLoading(true);
      setError(null);
      try {
        await deleteBudget(user.uid, budgetId);
        await loadBudgets();
      } catch (err: any) {
        setError(err.message || 'Erro ao excluir meta');
        console.error('Erro ao excluir meta:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, loadBudgets]
  );

  // ===== Relatórios =====
  const loadCashFlowData = useCallback(
    async (startDate: string, endDate: string): Promise<CashFlowData[]> => {
      if (!user?.uid) return [];

      try {
        return await getCashFlowData(user.uid, startDate, endDate);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar fluxo de caixa');
        console.error('Erro ao carregar fluxo de caixa:', err);
        return [];
      }
    },
    [user?.uid]
  );

  const loadCategorySummary = useCallback(
    async (
      startDate?: string,
      endDate?: string,
      type?: 'receita' | 'despesa'
    ): Promise<CategorySummary[]> => {
      if (!user?.uid) return [];

      try {
        return await getCategorySummary(user.uid, startDate, endDate, type);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar resumo por categoria');
        console.error('Erro ao carregar resumo por categoria:', err);
        return [];
      }
    },
    [user?.uid]
  );

  const loadMonthlyReport = useCallback(
    async (month: string): Promise<MonthlyReport | null> => {
      if (!user?.uid) return null;

      try {
        return await getMonthlyReport(user.uid, month);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar relatório mensal');
        console.error('Erro ao carregar relatório mensal:', err);
        return null;
      }
    },
    [user?.uid]
  );

  const getCategorySpent = useCallback(
    async (categoryId: string, month?: number, year?: number): Promise<number> => {
      if (!user?.uid) return 0;

      try {
        const now = new Date();
        const currentMonth = month ?? now.getMonth() + 1;
        const currentYear = year ?? now.getFullYear();

        const startDate = new Date(currentYear, currentMonth - 1, 1).toISOString().split('T')[0];
        const endDate = new Date(currentYear, currentMonth, 0, 23, 59, 59).toISOString().split('T')[0];

        const transactions = await getTransactions(user.uid, {
          categoryId,
          startDate,
          endDate,
          status: 'pago',
        });

        return transactions
          .filter(t => t.type === 'despesa')
          .reduce((sum, t) => sum + t.amount, 0);
      } catch (err: any) {
        console.error('Erro ao calcular gasto da categoria:', err);
        return 0;
      }
    },
    [user?.uid]
  );

  const syncConsultations = useCallback(async (): Promise<number> => {
    if (!user?.uid) return 0;

    setLoading(true);
    setError(null);
    try {
      const count = await syncConsultationsAsTransactions(user.uid);
      await loadTransactions();
      await loadStats(undefined, undefined, true);
      return count;
    } catch (err: any) {
      setError(err.message || 'Erro ao sincronizar consultas');
      console.error('Erro ao sincronizar consultas:', err);
      return 0;
    } finally {
      setLoading(false);
    }
  }, [user?.uid, loadTransactions, loadStats]);

  useEffect(() => {
    if (user?.uid) {
      loadStats(undefined, undefined, true);
      loadInvoices();
      loadCategories();
      loadAccounts();
      loadBudgets();
    }
  }, [user?.uid, loadStats, loadInvoices, loadCategories, loadAccounts, loadBudgets]);

  return {
    loading,
    error,
    stats,
    irReport,
    invoices,
    transactions,
    categories,
    accounts,
    budgets,
    loadStats,
    loadIRReport,
    loadInvoices,
    updateConsultationValue,
    getConsultationValue,
    // Transações
    loadTransactions,
    createNewTransaction,
    updateTransactionData,
    deleteTransactionData,
    // Categorias
    loadCategories,
    createNewCategory,
    updateCategoryData,
    deleteCategoryData,
    // Contas
    loadAccounts,
    createNewAccount,
    updateAccountData,
    deleteAccountData,
    // Metas
    loadBudgets,
    createNewBudget,
    updateBudgetData,
    deleteBudgetData,
    // Relatórios
    loadCashFlowData,
    loadCategorySummary,
    loadMonthlyReport,
    getCategorySpent,
    syncConsultations,
  };
}
